import React from 'react';
import { Checkbox, Group, Stack, UnstyledButton } from '@mantine/core';

export type GanttOptionalGridColumnKey =
  | 'wbs'
  | 'duration'
  | 'actualStart'
  | 'actualFinish'
  | 'actualDuration'
  | 'critical'
  | 'taskType'
  | 'locationType'
  | 'predecessors'
  | 'successors'
  | 'tracked'
  | 'assignees';

export type GanttVisibleOptionalColumns = Record<GanttOptionalGridColumnKey, boolean>;

export const DEFAULT_VISIBLE_OPTIONAL_COLUMNS: GanttVisibleOptionalColumns = {
  wbs: false,
  duration: true,
  actualStart: false,
  actualFinish: false,
  actualDuration: false,
  critical: false,
  taskType: false,
  locationType: true,
  predecessors: true,
  successors: false,
  tracked: false,
  assignees: false,
};

const COLUMN_OPTIONS: { key: GanttOptionalGridColumnKey; label: string }[] = [
  { key: 'wbs', label: 'WBS code' },
  { key: 'duration', label: 'Duration' },
  { key: 'actualStart', label: 'Actual start' },
  { key: 'actualFinish', label: 'Actual finish' },
  { key: 'actualDuration', label: 'Actual duration' },
  { key: 'critical', label: 'Critical' },
  { key: 'taskType', label: 'Task type' },
  { key: 'locationType', label: 'Location name' },
  { key: 'predecessors', label: 'Predecessor' },
  { key: 'successors', label: 'Successor' },
  { key: 'tracked', label: 'Tracked' },
  { key: 'assignees', label: 'Assignees' },
];

export interface GanttGridColumnTogglesProps {
  value: GanttVisibleOptionalColumns;
  onChange: React.Dispatch<React.SetStateAction<GanttVisibleOptionalColumns>>;
  /** checkbox = stacked list; chips = wireframe-style pill grid. */
  variant?: 'checkbox' | 'chips';
  /** Tighter spacing for Tools strip. */
  compact?: boolean;
}

export function GanttGridColumnToggles({
  value,
  onChange,
  variant = 'checkbox',
  compact = false,
}: GanttGridColumnTogglesProps) {
  if (variant === 'chips') {
    return (
      <Group gap={compact ? 4 : 8} wrap="wrap">
        {COLUMN_OPTIONS.map(({ key, label }) => {
          const active = value[key];
          return (
            <UnstyledButton
              key={key}
              type="button"
              className="gantt-popover-chip"
              aria-pressed={active}
              data-active={active ? 'true' : undefined}
              onClick={() => onChange((prev) => ({ ...prev, [key]: !prev[key] }))}
            >
              {label}
            </UnstyledButton>
          );
        })}
      </Group>
    );
  }

  return (
    <Stack gap={compact ? 0 : 6}>
      {COLUMN_OPTIONS.map(({ key, label }) => (
        <Checkbox
          key={key}
          size={compact ? 'xs' : 'sm'}
          label={label}
          checked={value[key]}
          className={compact ? 'gantt-columns-popover-row' : undefined}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            onChange((prev) => ({ ...prev, [key]: checked }));
          }}
        />
      ))}
    </Stack>
  );
}
